import { useState, useEffect, useCallback } from 'react';
import { DollarSign, BookOpen, Users, Home, TrendingUp, Clock } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const STAT_META = [
  { iconColor: '#16a34a', bg: '#dcfce7' },
  { iconColor: '#2563eb', bg: '#dbeafe' },
  { iconColor: '#7c3aed', bg: '#ede9fe' },
  { iconColor: '#d97706', bg: '#fef3c7' },
  { iconColor: '#dc2626', bg: '#fee2e2' },
  { iconColor: '#0891b2', bg: '#cffafe' },
];

const BAR_COLORS = {
  Confirmed: '#16a34a',
  Pending:   '#d97706',
  Completed: '#2563eb',
  Cancelled: '#dc2626',
};

const fmt = (d) => d ? d.toISOString().split('T')[0] : '';

const startOfWeek = () => {
  const d = new Date(); d.setHours(0,0,0,0);
  d.setDate(d.getDate() - d.getDay());
  return d;
};
const startOfMonth = () => { const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d; };
const startOfYear  = () => { const d = new Date(); d.setMonth(0,1); d.setHours(0,0,0,0); return d; };
const endOfToday   = () => { const d = new Date(); d.setHours(23,59,59,999); return d; };

const PRESETS = [
  { key: 'all',   label: 'All Time' },
  { key: 'today', label: 'Today' },
  { key: 'week',  label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'year',  label: 'This Year' },
  { key: 'custom',label: 'Custom' },
];

const OwnerDashboard = () => {
  const { user, hasPermission } = useAuth();
  const canViewReports = hasPermission('reports', 'view');

  const [preset, setPreset]       = useState('all');
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo]     = useState(null);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(true);

  const getDateRange = useCallback(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    const end   = endOfToday();
    switch (preset) {
      case 'today': return { from: fmt(today), to: fmt(end) };
      case 'week':  return { from: fmt(startOfWeek()), to: fmt(end) };
      case 'month': return { from: fmt(startOfMonth()), to: fmt(end) };
      case 'year':  return { from: fmt(startOfYear()), to: fmt(end) };
      case 'custom':
        if (customFrom && customTo) return { from: fmt(customFrom), to: fmt(customTo) };
        return {};
      default: return {};
    }
  }, [preset, customFrom, customTo]);

  useEffect(() => {
    if (!canViewReports) { setLoading(false); return; }
    if (preset === 'custom' && (!customFrom || !customTo)) return;
    setLoading(true);
    const { from, to } = getDateRange();
    const params = from && to ? `?from_date=${from}&to_date=${to}` : '';
    api.get(`/reports/summary${params}`)
      .then(res => setSummary(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [canViewReports, getDateRange, preset, customFrom, customTo]);

  const statCards = summary ? [
    { label: 'Total Revenue',    value: `₹${Number(summary.revenue?.total_revenue || 0).toLocaleString()}`,   icon: DollarSign, ...STAT_META[0] },
    { label: 'Total Bookings',   value: summary.bookings?.total_bookings || 0,                                 icon: BookOpen,   ...STAT_META[1] },
    { label: 'Total Customers',  value: summary.users?.total_customers || 0,                                   icon: Users,      ...STAT_META[2] },
    { label: 'Total Rooms',      value: summary.rooms?.total_rooms || 0,                                       icon: Home,       ...STAT_META[3] },
    { label: 'Pending Bookings', value: summary.bookings?.pending_bookings || 0,                               icon: Clock,      ...STAT_META[4] },
    { label: 'Pending Revenue',  value: `₹${Number(summary.revenue?.pending_revenue || 0).toLocaleString()}`, icon: TrendingUp, ...STAT_META[5] },
  ] : [];

  const total = summary?.bookings?.total_bookings || 0;
  const bookingStatusData = summary ? [
    { label: 'Confirmed', value: summary.bookings?.confirmed_bookings || 0 },
    { label: 'Pending',   value: summary.bookings?.pending_bookings   || 0 },
    { label: 'Completed', value: summary.bookings?.completed_bookings || 0 },
    { label: 'Cancelled', value: summary.bookings?.cancelled_bookings || 0 },
  ] : [];

  return (
    <div style={{ padding: '32px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '32px', fontWeight: '700', color: '#111827', margin: '0 0 8px' }}>
          {user?.user_type === 'staff' ? 'Portal Dashboard' : 'Owner Dashboard'}
        </h1>
        <p style={{ fontSize: '15px', color: '#6b7280', margin: 0 }}>
          Welcome back, <span style={{ color: '#16a34a', fontWeight: '600' }}>{user?.name}</span>!
          {canViewReports ? " Here's your business overview." : ' Use the sidebar to navigate.'}
        </p>
      </div>

      {/* Date filter bar */}
      {canViewReports && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 28 }}>
          {PRESETS.map(p => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              style={{
                padding: '7px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700,
                cursor: 'pointer', border: 'none',
                backgroundColor: preset === p.key ? '#16a34a' : '#ffffff',
                color: preset === p.key ? '#ffffff' : '#374151',
                boxShadow: preset === p.key ? '0 2px 8px rgba(22,163,74,0.3)' : '0 1px 4px rgba(0,0,0,0.08)',
                transition: 'all 0.15s',
              }}
            >
              {p.label}
            </button>
          ))}
          {preset === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DatePicker
                selected={customFrom}
                onChange={d => setCustomFrom(d)}
                selectsStart startDate={customFrom} endDate={customTo}
                maxDate={customTo || new Date()}
                dateFormat="dd MMM yyyy"
                placeholderText="From"
                customInput={<input style={{ padding: '7px 12px', borderRadius: 10, border: '1.5px solid #e5e7eb', fontSize: 13, width: 120, outline: 'none' }} />}
              />
              <span style={{ color: '#9ca3af', fontSize: 13 }}>→</span>
              <DatePicker
                selected={customTo}
                onChange={d => setCustomTo(d)}
                selectsEnd startDate={customFrom} endDate={customTo}
                minDate={customFrom}
                maxDate={new Date()}
                dateFormat="dd MMM yyyy"
                placeholderText="To"
                customInput={<input style={{ padding: '7px 12px', borderRadius: 10, border: '1.5px solid #e5e7eb', fontSize: 13, width: 120, outline: 'none' }} />}
              />
            </div>
          )}
          {summary?.date_filter && (
            <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 4 }}>
              {summary.date_filter.from_date} → {summary.date_filter.to_date}
            </span>
          )}
        </div>
      )}

      {/* No permission notice */}
      {!canViewReports && (
        <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '48px 32px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #f3f4f6', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Home size={28} color="#9ca3af" />
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '22px', fontWeight: '700', color: '#111827', margin: '0 0 10px' }}>Welcome to Burans Hikes Portal</h2>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            You're logged in as <strong>{user?.name}</strong>. Use the sidebar to access the sections available to your role.
          </p>
        </div>
      )}

      {/* Loading spinner */}
      {canViewReports && loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
          <div style={{ width: '44px', height: '44px', border: '4px solid #dcfce7', borderTop: '4px solid #16a34a', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      )}

      {/* Stats Grid */}
      {canViewReports && !loading && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px', marginBottom: '32px' }}>
            {statCards.map((card, i) => (
              <div key={i} style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #f3f4f6' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: card.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                  <card.icon size={22} color={card.iconColor} />
                </div>
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>{card.label}</p>
                <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '26px', fontWeight: '700', color: '#111827', margin: 0 }}>{card.value}</p>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
            {/* Booking Status Breakdown */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #f3f4f6' }}>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 24px' }}>Booking Status Breakdown</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {bookingStatusData.map(item => {
                  const pct   = total ? Math.round((item.value / total) * 100) : 0;
                  const color = BAR_COLORS[item.label] || '#16a34a';
                  return (
                    <div key={item.label}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '14px', color: '#6b7280' }}>{item.label}</span>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{item.value}</span>
                      </div>
                      <div style={{ width: '100%', height: '8px', backgroundColor: '#f3f4f6', borderRadius: '99px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, minWidth: item.value > 0 ? '6px' : '0', backgroundColor: color, borderRadius: '99px', transition: 'width 0.5s ease' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick Stats */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #f3f4f6' }}>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 8px' }}>Quick Stats</h2>
              <div>
                {[
                  { label: 'Active Agents',          value: summary?.users?.total_agents || 0 },
                  { label: 'Available Rooms',         value: summary?.rooms?.available_rooms || 0 },
                  { label: 'Occupied Rooms',          value: summary?.rooms?.occupied_rooms || 0 },
                  { label: 'Agent Commissions Owed',  value: `₹${Number(summary?.commissions?.pending_commissions || 0).toLocaleString()}` },
                ].map((item, i, arr) => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: i < arr.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>{item.label}</span>
                    <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '22px', fontWeight: '700', color: '#111827' }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OwnerDashboard;
